/**
 * Core Orchestrator
 *
 * Pipeline engine that runs analyses in parallel and aggregates results.
 * Wraps Forma SDK with typed interfaces.
 */

export {
  parseReportAgentPackage,
  parseSimulationEvidence,
  parseStudyRequest,
  type EvidenceFinding,
  type EvidenceGrid,
  type EvidenceMetric,
  type EvidenceStatus,
  type ReportAgentPackage,
  type SimulationEvidence,
  type StudyRequest,
} from './contracts';

export {
  parseThermalModelRequest,
  type BuildingUnit,
  type BuildingUse,
  type ClimateZone,
  type Construction,
  type GlazingProperties,
  type MaterialLayer,
  type MaterialSource,
  type SurfaceInput,
  type SurfaceKind,
  type ThermalModelRequest,
  type ThermalProject,
} from './contracts-thermal';

export {
  emptyNormativeDataset,
  missingNormativeFields,
  parseNormativeDataset,
  type GradeThreshold,
  type NormativeDataset,
  type NormativeField,
  type NormativeSetpoints,
  type SurfaceResistance,
} from './normative-dataset';

export { routeStudies } from './analysis-router';

export {
  collectStudyResults,
  type CollectedBlocker,
  type CollectedStudyResults,
  type CollectStudyResultsInput,
  type ResultsSummary,
} from './results-collector';

export {
  reconcileStudyCoverage,
  type ReconcileStudyCoverageInput,
  type StudyCoverage,
} from './study-coverage';

export { analyzeGeometryReadiness, analyzeSunShadowScreening } from './screening';

export { analyzeNoiseScreening } from './noise-screening';

export { analyzeWindComfortScreening } from './wind-screening';

export {
  collectNativeScreening,
  type CollectNativeScreeningOptions,
  type NativeNoiseScreeningRequest,
  type NativeScreeningFailure,
  type NativeScreeningResult,
  type NativeSunScreeningRequest,
} from './native-screening';

export { buildWindHeightMaps, type BuildWindHeightMapsOptions } from './wind-height-map';

export {
  collectWindComfortScreening,
  DEFAULT_WIND_COMFORT_SCALE,
  type CollectWindComfortScreeningOptions,
  type WindComfortScreeningResult,
} from './wind-comfort-screening';

export {
  pullFullFormaSnapshot,
  DEFAULT_PULL_CATEGORIES,
  DEFAULT_MAX_DETAIL_ELEMENTS,
  type FormaApiCallReport,
  type FormaApiCallStatus,
  type FormaElementSnapshot,
  type FormaFullPullClient,
  type FormaFullSnapshot,
  type FormaGeometryCategorySnapshot,
  type FullFormaPullResult,
  type PullFullFormaSnapshotOptions,
} from './full-pull';

export const VERSION = '0.1.0';

export function placeholder(): string {
  return 'core-orchestrator scaffolding ready';
}

/** Municipality identifier selected by the user in the Forma panel. */
export type MunicipalityId = string;

/** Main project snapshot passed between the Forma extension, analyses, and reports. */
export interface Project {
  /** Stable internal project identifier from Forma or the plugin session. */
  id: string;
  /** Human-readable project name used in generated report metadata. */
  name: string;
  /** IANA timezone for date-sensitive analyses such as sun exposure. */
  timezone: string;
  /** Municipality currently selected for the compliance package. */
  municipalityId: MunicipalityId;
  /** Site-level geometry and location context. */
  site: Site;
  /** Buildings included in the compliance package. */
  buildings: readonly Building[];
}

/** Site-level context extracted from the Forma project. */
export interface Site {
  /** Stable site identifier from Forma or the plugin session. */
  id: string;
  /** Optional address label shown in operator-facing UI and reports. */
  address?: string;
  /** Forma geometry paths that represent terrain or parcel context. */
  terrainPaths: readonly string[];
  /** Forma geometry paths that represent the parcel or study boundary. */
  boundaryPaths: readonly string[];
  /** Optional geographic anchor for map and climate integrations. */
  location?: {
    latitude: number;
    longitude: number;
  };
}

/** Building-level context used by orchestration and report generation. */
export interface Building {
  /** Stable building identifier from Forma or the plugin session. */
  id: string;
  /** Human-readable building label. */
  name: string;
  /** Forma geometry paths included in this building. */
  elementPaths: readonly string[];
  /** Operator-selected use type; not a regulatory classification by itself. */
  useType: 'residential' | 'office' | 'mixed-use' | 'public' | 'unknown';
  /** Optional number of above-grade stories when available from Forma or user input. */
  storiesAboveGrade?: number;
  /** Optional total building height in meters. */
  heightMeters?: number;
  /** Optional gross floor area in square meters. */
  grossFloorAreaM2?: number;
}

export type GeometryEvidenceConfidence = 'high' | 'medium' | 'low';

export interface GeometryBoundingBox {
  minX: number;
  minY: number;
  minZ?: number;
  maxX: number;
  maxY: number;
  maxZ?: number;
}

export interface GeometryFootprintSnapshot {
  areaM2?: number;
  bbox?: GeometryBoundingBox;
  vertices?: readonly (readonly [number, number])[];
}

export interface GeometryMeshManifest {
  triangleCount: number;
  vertexCount?: number;
  hash?: string;
}

export interface GeometryElementSnapshot {
  path: string;
  category: string;
  urn?: string;
  name?: string;
  footprint?: GeometryFootprintSnapshot;
  bbox?: GeometryBoundingBox;
  heightMeters?: number;
  grossFloorAreaM2?: number;
  storiesAboveGrade?: number;
  mesh?: GeometryMeshManifest;
  confidence: GeometryEvidenceConfidence;
  missingData: readonly string[];
}

export interface GeometryExchangeSummary {
  buildingCount: number;
  footprintCount: number;
  grossFloorAreaM2?: number;
  bbox?: GeometryBoundingBox;
  heightRangeMeters?: {
    min: number;
    max: number;
  };
  confidence: GeometryEvidenceConfidence;
  missingData: readonly string[];
}

export interface GeometryExchange {
  schemaVersion: 'geometry-exchange/v1';
  source: 'forma-sdk' | 'fixture';
  project: {
    formaProjectId: string;
    proposalId: string;
    proposalRevision?: string;
    rootUrn: string;
    srid?: number;
    refPoint?: readonly [number, number];
    timezone?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  selection: {
    selectedPaths: readonly string[];
  };
  elements: readonly GeometryElementSnapshot[];
  summary: GeometryExchangeSummary;
}

export function parseGeometryExchange(value: unknown): GeometryExchange {
  const exchange = assertObject(value, 'GeometryExchange');
  const schemaVersion = assertStringField(exchange, 'schemaVersion', 'GeometryExchange');

  if (schemaVersion !== 'geometry-exchange/v1') {
    throw new Error(`Unsupported geometry exchange schema: ${schemaVersion}`);
  }

  const source = assertStringField(exchange, 'source', 'GeometryExchange');

  if (source !== 'forma-sdk' && source !== 'fixture') {
    throw new Error(`Unsupported geometry exchange source: ${source}`);
  }

  const project = assertObjectField(exchange, 'project', 'GeometryExchange');
  assertStringField(project, 'formaProjectId', 'GeometryExchange.project');
  assertStringField(project, 'proposalId', 'GeometryExchange.project');
  assertStringField(project, 'rootUrn', 'GeometryExchange.project');
  assertOptionalNumberField(project, 'srid', 'GeometryExchange.project');
  assertOptionalNumberTupleField(project, 'refPoint', 'GeometryExchange.project', 2);
  validateOptionalLocation(project, 'GeometryExchange.project');

  const selection = assertObjectField(exchange, 'selection', 'GeometryExchange');
  assertStringArrayField(selection, 'selectedPaths', 'GeometryExchange.selection');

  const elements = assertArrayField(exchange, 'elements', 'GeometryExchange');
  elements.forEach((element, index) => {
    const elementRecord = assertObject(element, `GeometryExchange.elements[${index}]`);
    assertStringField(elementRecord, 'path', `GeometryExchange.elements[${index}]`);
    assertStringField(elementRecord, 'category', `GeometryExchange.elements[${index}]`);
    assertConfidenceField(elementRecord, 'confidence', `GeometryExchange.elements[${index}]`);
    assertStringArrayField(elementRecord, 'missingData', `GeometryExchange.elements[${index}]`);
    assertOptionalNumberField(elementRecord, 'heightMeters', `GeometryExchange.elements[${index}]`);
    assertOptionalNumberField(
      elementRecord,
      'grossFloorAreaM2',
      `GeometryExchange.elements[${index}]`,
    );
    assertOptionalNumberField(
      elementRecord,
      'storiesAboveGrade',
      `GeometryExchange.elements[${index}]`,
    );
    validateOptionalBoundingBox(elementRecord, 'bbox', `GeometryExchange.elements[${index}]`);
    validateOptionalFootprint(elementRecord, `GeometryExchange.elements[${index}]`);
    validateOptionalMesh(elementRecord, `GeometryExchange.elements[${index}]`);
  });

  const summary = assertObjectField(exchange, 'summary', 'GeometryExchange');
  assertNumberField(summary, 'buildingCount', 'GeometryExchange.summary');
  assertNumberField(summary, 'footprintCount', 'GeometryExchange.summary');
  assertOptionalNumberField(summary, 'grossFloorAreaM2', 'GeometryExchange.summary');
  assertConfidenceField(summary, 'confidence', 'GeometryExchange.summary');
  assertStringArrayField(summary, 'missingData', 'GeometryExchange.summary');
  validateOptionalBoundingBox(summary, 'bbox', 'GeometryExchange.summary');
  validateOptionalHeightRange(summary, 'GeometryExchange.summary');

  return value as GeometryExchange;
}

/** Executable analysis unit that the core pipeline can run independently. */
export interface PipelineAnalysis<Value = unknown> {
  /** Stable analysis identifier used in logs, UI status, and reports. */
  id: string;
  /** Runs the analysis and resolves with its normalized result payload. */
  run(): Promise<Value>;
}

/** Result for one pipeline analysis, preserving partial failures. */
export type PipelineAnalysisResult<Value = unknown> =
  | {
      id: string;
      status: 'fulfilled';
      value: Value;
    }
  | {
      id: string;
      status: 'rejected';
      error: unknown;
    };

/** Aggregated pipeline run output. */
export interface PipelineRunResult<Value = unknown> {
  /** One result per input analysis, in the same order. */
  results: readonly PipelineAnalysisResult<Value>[];
  /** Number of analyses that completed successfully. */
  succeeded: number;
  /** Number of analyses that rejected. */
  failed: number;
}

/** Runs independent analyses in parallel and keeps successful and failed results. */
export class Pipeline<Value = unknown> {
  readonly #analyses: readonly PipelineAnalysis<Value>[];

  constructor(analyses: readonly PipelineAnalysis<Value>[]) {
    this.#analyses = analyses;
  }

  async run(): Promise<PipelineRunResult<Value>> {
    const settled = await Promise.allSettled(this.#analyses.map((analysis) => analysis.run()));
    const results = settled.map((result, index): PipelineAnalysisResult<Value> => {
      const analysis = this.#analyses[index];

      if (analysis === undefined) {
        throw new Error('Pipeline analysis index mismatch.');
      }

      return result.status === 'fulfilled'
        ? { id: analysis.id, status: 'fulfilled', value: result.value }
        : { id: analysis.id, status: 'rejected', error: result.reason };
    });
    const succeeded = results.filter((result) => result.status === 'fulfilled').length;

    return {
      results,
      succeeded,
      failed: results.length - succeeded,
    };
  }
}

export interface FormaGeometryPathsClient {
  getPathsByCategory(request: { category: string; urn?: string }): Promise<string[]>;
}

export interface FormaGeometryClient {
  geometry: FormaGeometryPathsClient;
}

export type PathsByCategory<Category extends string = string> = Record<Category, string[]>;

export interface GetPathsByCategoryOptions<Category extends string = string> {
  forma: FormaGeometryClient;
  categories: readonly Category[];
  urn?: string;
}

export async function getPathsByCategory<Category extends string>(
  options: GetPathsByCategoryOptions<Category>,
): Promise<PathsByCategory<Category>> {
  const entries = await Promise.all(
    options.categories.map(async (category) => {
      const request = options.urn === undefined ? { category } : { category, urn: options.urn };
      const paths = await options.forma.geometry.getPathsByCategory(request);

      return [category, paths] as const;
    }),
  );

  return Object.fromEntries(entries) as PathsByCategory<Category>;
}

export interface FormaProjectMetadata {
  countryCode: string;
  srid: number;
  refPoint: [number, number];
  projString: string;
  timezone: string;
}

export interface FormaProjectSnapshotClient extends FormaGeometryClient {
  getProjectId(): string;
  project: {
    get(): Promise<FormaProjectMetadata>;
    getGeoLocation(): Promise<[number, number] | undefined>;
  };
  proposal: {
    getId(): Promise<string>;
    getRootUrn(): Promise<string>;
  };
  selection: {
    getSelection(): Promise<string[]>;
  };
}

export interface BuildProjectSnapshotOptions {
  forma: FormaProjectSnapshotClient;
  municipalityId: MunicipalityId;
  projectName?: string;
  buildingUseType?: Building['useType'];
  buildingCategories?: readonly string[];
  terrainCategories?: readonly string[];
  boundaryCategories?: readonly string[];
}

export async function loadAutoFormaProjectSnapshotClient(): Promise<FormaProjectSnapshotClient> {
  const { Forma } = await import('forma-embedded-view-sdk/auto');

  return Forma;
}

/**
 * Loads the auto-initialized Forma SDK as a native-analysis client. The live
 * SDK exposes the analysis surface (list/trigger/get) consumed by the native
 * screening wrappers; this mirrors {@link loadAutoFormaProjectSnapshotClient}
 * so the extension can drive native analyses without importing the SDK directly.
 */
export async function loadAutoFormaNativeAnalysisClient(): Promise<FormaNativeAnalysisClient> {
  const { Forma } = await import('forma-embedded-view-sdk/auto');

  return Forma as unknown as FormaNativeAnalysisClient;
}

/** The live SDK surfaces consumed by the extension's live screening toggle. */
export type FormaLiveScreeningClient = FormaNativeAnalysisClient &
  FormaWindPredictionClient &
  FormaGeometryExchangeClient;

/**
 * Loads the auto-initialized Forma SDK as a combined live-screening client. The
 * single live `Forma` object exposes the analysis, prediction, and geometry
 * surfaces at once, so one loader backs sun/noise/wind screening and geometry
 * extraction without importing the SDK directly in the extension.
 */
export async function loadAutoFormaLiveClient(): Promise<FormaLiveScreeningClient> {
  const { Forma } = await import('forma-embedded-view-sdk/auto');

  return Forma as unknown as FormaLiveScreeningClient;
}

/**
 * Loads the auto-initialized Forma SDK as a full-pull client. The live `Forma`
 * object also exposes the project country/timezone and sun surfaces consumed by
 * {@link pullFullFormaSnapshot}, so this single loader backs the full data pull
 * + API verification pass.
 */
export async function loadAutoFormaFullPullClient(): Promise<
  import('./full-pull').FormaFullPullClient
> {
  const { Forma } = await import('forma-embedded-view-sdk/auto');

  return Forma as unknown as import('./full-pull').FormaFullPullClient;
}

const DEFAULT_BUILDING_CATEGORIES = ['building', 'buildings'] as const;
const DEFAULT_TERRAIN_CATEGORIES = ['terrain'] as const;
const DEFAULT_BOUNDARY_CATEGORIES = ['site_limit'] as const;

export async function buildProjectSnapshot(options: BuildProjectSnapshotOptions): Promise<Project> {
  const [metadata, geoLocation, proposalId, rootUrn, selectedPaths] = await Promise.all([
    options.forma.project.get(),
    options.forma.project.getGeoLocation(),
    options.forma.proposal.getId(),
    options.forma.proposal.getRootUrn(),
    options.forma.selection.getSelection(),
  ]);
  const [terrainPaths, boundaryPaths, categoryBuildingPaths] = await Promise.all([
    getPathsForCategories(
      options.forma.geometry,
      options.terrainCategories ?? DEFAULT_TERRAIN_CATEGORIES,
      rootUrn,
    ),
    getPathsForCategories(
      options.forma.geometry,
      options.boundaryCategories ?? DEFAULT_BOUNDARY_CATEGORIES,
      rootUrn,
    ),
    getPathsForCategories(
      options.forma.geometry,
      options.buildingCategories ?? DEFAULT_BUILDING_CATEGORIES,
      rootUrn,
    ),
  ]);
  const buildingPaths = selectedPaths.length > 0 ? selectedPaths : categoryBuildingPaths;
  const location =
    geoLocation === undefined
      ? undefined
      : {
          latitude: geoLocation[0],
          longitude: geoLocation[1],
        };

  return {
    id: options.forma.getProjectId(),
    name: options.projectName ?? options.forma.getProjectId(),
    timezone: metadata.timezone,
    municipalityId: options.municipalityId,
    site:
      location === undefined
        ? { id: proposalId, terrainPaths, boundaryPaths }
        : { id: proposalId, terrainPaths, boundaryPaths, location },
    buildings: buildingPaths.map((path, index) => ({
      id: path,
      name: `Building ${index + 1}`,
      elementPaths: [path],
      useType: options.buildingUseType ?? 'unknown',
    })),
  };
}

async function getPathsForCategories(
  geometry: FormaGeometryPathsClient,
  categories: readonly string[],
  urn: string,
): Promise<string[]> {
  const paths = await Promise.all(
    categories.map((category) => geometry.getPathsByCategory({ category, urn })),
  );

  return [...new Set(paths.flat())];
}

/** Footprint polygon returned by the Forma geometry SDK for a single element. */
export interface FormaFootprint {
  type: 'LineString' | 'Polygon';
  coordinates: ReadonlyArray<readonly [number, number]>;
}

/** Aggregate area metrics returned by Forma's native areaMetrics surface. */
export interface FormaAreaMetrics {
  builtInMetrics?: {
    grossFloorArea?: number;
    buildingCoverage?: number;
    siteArea?: number;
  };
}

/** Forma geometry surface needed to extract element-level evidence. */
export interface FormaGeometryDetailClient extends FormaGeometryPathsClient {
  getFootprint(request: { path: string; urn?: string }): Promise<FormaFootprint | undefined>;
  getTriangles(request: { path: string; urn?: string }): Promise<Float32Array | undefined>;
}

/** Live Forma SDK surface consumed to build a geometry-exchange/v1 snapshot. */
export interface FormaGeometryExchangeClient {
  getProjectId(): string;
  project: {
    get(): Promise<FormaProjectMetadata>;
    getGeoLocation(): Promise<[number, number] | undefined>;
  };
  proposal: {
    getId(): Promise<string>;
    getRootUrn(): Promise<string>;
  };
  selection: {
    getSelection(): Promise<string[]>;
  };
  geometry: FormaGeometryDetailClient;
  areaMetrics?: {
    calculate(request: { paths?: string[] }): Promise<FormaAreaMetrics>;
  };
}

export interface BuildGeometryExchangeOptions {
  forma: FormaGeometryExchangeClient;
  buildingCategories?: readonly string[];
}

/**
 * Extracts a geometry-exchange/v1 snapshot from the live Forma SDK, reusing the
 * native geometry surface (paths, footprint, triangle mesh) plus optional area
 * metrics. Failed or empty SDK calls degrade to missing-data flags and lower
 * confidence instead of throwing, so the pipeline stays alive on partial models.
 */
export async function buildGeometryExchange(
  options: BuildGeometryExchangeOptions,
): Promise<GeometryExchange> {
  const { forma } = options;
  const [metadata, geoLocation, proposalId, rootUrn, selectedPaths] = await Promise.all([
    forma.project.get(),
    forma.project.getGeoLocation(),
    forma.proposal.getId(),
    forma.proposal.getRootUrn(),
    forma.selection.getSelection(),
  ]);
  const categoryPaths = await getPathsForCategories(
    forma.geometry,
    options.buildingCategories ?? DEFAULT_BUILDING_CATEGORIES,
    rootUrn,
  );
  const buildingPaths = selectedPaths.length > 0 ? selectedPaths : categoryPaths;
  const elements = await Promise.all(
    buildingPaths.map((path) => buildGeometryElementSnapshot(forma.geometry, path, rootUrn)),
  );
  const areaMetrics =
    forma.areaMetrics === undefined
      ? undefined
      : await safeAreaMetrics(forma.areaMetrics, buildingPaths);
  const location =
    geoLocation === undefined ? undefined : { latitude: geoLocation[0], longitude: geoLocation[1] };

  return {
    schemaVersion: 'geometry-exchange/v1',
    source: 'forma-sdk',
    project: {
      formaProjectId: forma.getProjectId(),
      proposalId,
      rootUrn,
      srid: metadata.srid,
      refPoint: metadata.refPoint,
      timezone: metadata.timezone,
      ...(location === undefined ? {} : { location }),
    },
    selection: { selectedPaths },
    elements,
    summary: buildGeometryExchangeSummary(elements, areaMetrics),
  };
}

async function buildGeometryElementSnapshot(
  geometry: FormaGeometryDetailClient,
  path: string,
  urn: string,
): Promise<GeometryElementSnapshot> {
  const [footprint, triangles] = await Promise.all([
    safeGetFootprint(geometry, path, urn),
    safeGetTriangles(geometry, path, urn),
  ]);
  const footprintSnapshot = footprint === undefined ? undefined : buildFootprintSnapshot(footprint);
  const meshBbox = triangles === undefined ? undefined : computeMeshBoundingBox(triangles);
  const bbox = meshBbox ?? footprintSnapshot?.bbox;
  const heightMeters =
    meshBbox === undefined || meshBbox.minZ === undefined || meshBbox.maxZ === undefined
      ? undefined
      : roundMeters(meshBbox.maxZ - meshBbox.minZ);
  const missingData: string[] = [];

  if (footprintSnapshot === undefined) {
    missingData.push('footprint');
  }

  if (triangles === undefined) {
    missingData.push('mesh');
  }

  if (heightMeters === undefined) {
    missingData.push('heightMeters');
  }

  const confidence: GeometryEvidenceConfidence =
    footprintSnapshot !== undefined && meshBbox !== undefined
      ? 'high'
      : footprintSnapshot === undefined && meshBbox === undefined
        ? 'low'
        : 'medium';

  return {
    path,
    category: 'building',
    confidence,
    missingData,
    ...(footprintSnapshot === undefined ? {} : { footprint: footprintSnapshot }),
    ...(bbox === undefined ? {} : { bbox }),
    ...(heightMeters === undefined ? {} : { heightMeters }),
    ...(triangles === undefined
      ? {}
      : {
          mesh: {
            triangleCount: Math.floor(triangles.length / 9),
            vertexCount: Math.floor(triangles.length / 3),
          },
        }),
  };
}

function buildFootprintSnapshot(footprint: FormaFootprint): GeometryFootprintSnapshot {
  const vertices = footprint.coordinates;
  const bbox = boundingBoxFromVertices(vertices);

  return {
    areaM2: roundMeters(polygonAreaM2(vertices)),
    vertices,
    ...(bbox === undefined ? {} : { bbox }),
  };
}

function polygonAreaM2(vertices: ReadonlyArray<readonly [number, number]>): number {
  if (vertices.length < 3) {
    return 0;
  }

  let sum = 0;

  for (let index = 0; index < vertices.length; index += 1) {
    const current = vertices[index];
    const next = vertices[(index + 1) % vertices.length];

    if (current === undefined || next === undefined) {
      continue;
    }

    sum += current[0] * next[1] - next[0] * current[1];
  }

  return Math.abs(sum) / 2;
}

function boundingBoxFromVertices(
  vertices: ReadonlyArray<readonly [number, number]>,
): GeometryBoundingBox | undefined {
  const first = vertices[0];

  if (first === undefined) {
    return undefined;
  }

  let minX = first[0];
  let minY = first[1];
  let maxX = first[0];
  let maxY = first[1];

  for (const [x, y] of vertices) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return { minX, minY, maxX, maxY };
}

function computeMeshBoundingBox(triangles: Float32Array): GeometryBoundingBox | undefined {
  if (triangles.length < 9) {
    return undefined;
  }

  let minX = triangles[0] ?? 0;
  let minY = triangles[1] ?? 0;
  let minZ = triangles[2] ?? 0;
  let maxX = minX;
  let maxY = minY;
  let maxZ = minZ;

  for (let offset = 0; offset + 2 < triangles.length; offset += 3) {
    const x = triangles[offset] ?? 0;
    const y = triangles[offset + 1] ?? 0;
    const z = triangles[offset + 2] ?? 0;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    minZ = Math.min(minZ, z);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    maxZ = Math.max(maxZ, z);
  }

  return { minX, minY, minZ, maxX, maxY, maxZ };
}

function buildGeometryExchangeSummary(
  elements: readonly GeometryElementSnapshot[],
  areaMetrics: FormaAreaMetrics | undefined,
): GeometryExchangeSummary {
  const footprintCount = elements.filter((element) => element.footprint !== undefined).length;
  const grossFloorAreaM2 = areaMetrics?.builtInMetrics?.grossFloorArea;
  const heights = elements
    .map((element) => element.heightMeters)
    .filter((value): value is number => value !== undefined);
  const bbox = unionBoundingBox(
    elements
      .map((element) => element.bbox)
      .filter((value): value is GeometryBoundingBox => value !== undefined),
  );
  const missingData = [...new Set(elements.flatMap((element) => element.missingData))];

  if (grossFloorAreaM2 === undefined) {
    missingData.push('grossFloorArea');
  }

  if (elements.length === 0) {
    missingData.push('buildings');
  }

  return {
    buildingCount: elements.length,
    footprintCount,
    confidence: summaryConfidence(elements),
    missingData: [...new Set(missingData)],
    ...(grossFloorAreaM2 === undefined ? {} : { grossFloorAreaM2: roundMeters(grossFloorAreaM2) }),
    ...(bbox === undefined ? {} : { bbox }),
    ...(heights.length === 0
      ? {}
      : { heightRangeMeters: { min: Math.min(...heights), max: Math.max(...heights) } }),
  };
}

function summaryConfidence(
  elements: readonly GeometryElementSnapshot[],
): GeometryEvidenceConfidence {
  if (elements.length === 0 || elements.some((element) => element.confidence === 'low')) {
    return 'low';
  }

  return elements.every((element) => element.confidence === 'high') ? 'high' : 'medium';
}

function unionBoundingBox(boxes: readonly GeometryBoundingBox[]): GeometryBoundingBox | undefined {
  const first = boxes[0];

  if (first === undefined) {
    return undefined;
  }

  return boxes
    .slice(1)
    .reduce<GeometryBoundingBox>((acc, box) => mergeBoundingBox(acc, box), first);
}

function mergeBoundingBox(a: GeometryBoundingBox, b: GeometryBoundingBox): GeometryBoundingBox {
  const minZ = minOptional(a.minZ, b.minZ);
  const maxZ = maxOptional(a.maxZ, b.maxZ);

  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
    ...(minZ === undefined ? {} : { minZ }),
    ...(maxZ === undefined ? {} : { maxZ }),
  };
}

function minOptional(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) {
    return b;
  }

  if (b === undefined) {
    return a;
  }

  return Math.min(a, b);
}

function maxOptional(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) {
    return b;
  }

  if (b === undefined) {
    return a;
  }

  return Math.max(a, b);
}

async function safeGetFootprint(
  geometry: FormaGeometryDetailClient,
  path: string,
  urn: string,
): Promise<FormaFootprint | undefined> {
  try {
    const footprint = await geometry.getFootprint({ path, urn });

    return footprint !== undefined && footprint.coordinates.length > 0 ? footprint : undefined;
  } catch {
    return undefined;
  }
}

async function safeGetTriangles(
  geometry: FormaGeometryDetailClient,
  path: string,
  urn: string,
): Promise<Float32Array | undefined> {
  try {
    const triangles = await geometry.getTriangles({ path, urn });

    return triangles !== undefined && triangles.length >= 9 ? triangles : undefined;
  } catch {
    return undefined;
  }
}

async function safeAreaMetrics(
  areaMetrics: NonNullable<FormaGeometryExchangeClient['areaMetrics']>,
  paths: readonly string[],
): Promise<FormaAreaMetrics | undefined> {
  try {
    return await areaMetrics.calculate({ paths: [...paths] });
  } catch {
    return undefined;
  }
}

function roundMeters(value: number): number {
  return Math.round(value * 100) / 100;
}

function assertObject(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object.`);
  }

  return value as Record<string, unknown>;
}

function assertObjectField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): Record<string, unknown> {
  return assertObject(record[field], `${label}.${field}`);
}

function assertArrayField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): readonly unknown[] {
  const value = record[field];

  if (!Array.isArray(value)) {
    throw new Error(`${label}.${field} must be an array.`);
  }

  return value;
}

function assertStringField(record: Record<string, unknown>, field: string, label: string): string {
  const value = record[field];

  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${label}.${field} must be a non-empty string.`);
  }

  return value;
}

function assertNumberField(record: Record<string, unknown>, field: string, label: string): number {
  const value = record[field];

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label}.${field} must be a finite number.`);
  }

  return value;
}

function assertOptionalNumberField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  if (record[field] !== undefined) {
    assertNumberField(record, field, label);
  }
}

function assertOptionalNumberTupleField(
  record: Record<string, unknown>,
  field: string,
  label: string,
  length: number,
): void {
  const value = record[field];

  if (value === undefined) {
    return;
  }

  if (
    !Array.isArray(value) ||
    value.length !== length ||
    !value.every((item) => typeof item === 'number' && Number.isFinite(item))
  ) {
    throw new Error(`${label}.${field} must be a ${length}-item finite number tuple.`);
  }
}

function assertStringArrayField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = assertArrayField(record, field, label);

  if (!value.every((item) => typeof item === 'string')) {
    throw new Error(`${label}.${field} must contain only strings.`);
  }
}

function assertConfidenceField(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  const value = assertStringField(record, field, label);

  if (value !== 'high' && value !== 'medium' && value !== 'low') {
    throw new Error(`${label}.${field} must be high, medium, or low.`);
  }
}

function validateOptionalLocation(record: Record<string, unknown>, label: string): void {
  if (record.location === undefined) {
    return;
  }

  const location = assertObjectField(record, 'location', label);
  assertNumberField(location, 'latitude', `${label}.location`);
  assertNumberField(location, 'longitude', `${label}.location`);
}

function validateOptionalFootprint(record: Record<string, unknown>, label: string): void {
  if (record.footprint === undefined) {
    return;
  }

  const footprint = assertObjectField(record, 'footprint', label);
  assertOptionalNumberField(footprint, 'areaM2', `${label}.footprint`);
  validateOptionalBoundingBox(footprint, 'bbox', `${label}.footprint`);

  if (footprint.vertices !== undefined) {
    const vertices = assertArrayField(footprint, 'vertices', `${label}.footprint`);
    vertices.forEach((vertex, index) => {
      if (
        !Array.isArray(vertex) ||
        vertex.length !== 2 ||
        !vertex.every((item) => typeof item === 'number' && Number.isFinite(item))
      ) {
        throw new Error(`${label}.footprint.vertices[${index}] must be a 2D point tuple.`);
      }
    });
  }
}

function validateOptionalBoundingBox(
  record: Record<string, unknown>,
  field: string,
  label: string,
): void {
  if (record[field] === undefined) {
    return;
  }

  const bbox = assertObjectField(record, field, label);
  assertNumberField(bbox, 'minX', `${label}.${field}`);
  assertNumberField(bbox, 'minY', `${label}.${field}`);
  assertNumberField(bbox, 'maxX', `${label}.${field}`);
  assertNumberField(bbox, 'maxY', `${label}.${field}`);
  assertOptionalNumberField(bbox, 'minZ', `${label}.${field}`);
  assertOptionalNumberField(bbox, 'maxZ', `${label}.${field}`);
}

function validateOptionalMesh(record: Record<string, unknown>, label: string): void {
  if (record.mesh === undefined) {
    return;
  }

  const mesh = assertObjectField(record, 'mesh', label);
  assertNumberField(mesh, 'triangleCount', `${label}.mesh`);
  assertOptionalNumberField(mesh, 'vertexCount', `${label}.mesh`);

  if (mesh.hash !== undefined && typeof mesh.hash !== 'string') {
    throw new Error(`${label}.mesh.hash must be a string.`);
  }
}

function validateOptionalHeightRange(record: Record<string, unknown>, label: string): void {
  if (record.heightRangeMeters === undefined) {
    return;
  }

  const heightRange = assertObjectField(record, 'heightRangeMeters', label);
  assertNumberField(heightRange, 'min', `${label}.heightRangeMeters`);
  assertNumberField(heightRange, 'max', `${label}.heightRangeMeters`);
}

export interface FormaAnalysisRecord {
  analysisId: string;
  analysisType: string;
  createdAt?: number;
  elementUrn?: string;
  proposalId?: string;
  proposalRevision?: string;
  status: string;
  updatedAt?: number;
  [metadata: string]: unknown;
}

export interface FormaAnalysisGroundGrid {
  grid: Float32Array | Uint8Array;
  width: number;
  height: number;
  x0: number;
  y0: number;
  resolution: number;
  mask?: Uint8Array;
}

export interface FormaSunAnalysisParameters {
  selectedElementPaths: string[];
  sunDate: {
    month: number;
    date: number;
  };
  timezone: string;
  geoLocation: [number, number];
  groundResolution: number;
  sunPositionsPerHour: number;
}

export interface FormaNoiseAnalysisParameters {
  selectedElementPaths: string[];
  groundResolution: number;
  hasRoad: boolean;
  hasRail: boolean;
}

export interface FormaSunAnalysis extends FormaAnalysisRecord {
  completedAt?: number;
  parameters: FormaSunAnalysisParameters;
}

export interface FormaNoiseAnalysis extends FormaAnalysisRecord {
  completedAt?: number;
  parameters: FormaNoiseAnalysisParameters;
}

export interface ListNativeAnalysesRequest {
  authcontext?: string;
  elementUrnPrefix?: string;
  analysisTypes?: readonly string[];
}

export interface FormaAnalysisClient {
  analysis: {
    triggerSun(request: {
      selectedElementPaths: string[];
      month: number;
      date: number;
      rootUrn?: string;
    }): Promise<FormaAnalysisRecord>;
    getGroundGrid(request: {
      analysis: FormaAnalysisRecord;
    }): Promise<FormaAnalysisGroundGrid | undefined>;
  };
}

export interface FormaNativeAnalysisClient extends FormaAnalysisClient {
  analysis: FormaAnalysisClient['analysis'] & {
    list(request: {
      authcontext?: string;
      elementUrnPrefix?: string;
      analysisTypes?: string[];
    }): Promise<FormaAnalysisRecord[]>;
    triggerNoise(request: {
      selectedElementPaths: string[];
      rootUrn?: string;
    }): Promise<FormaAnalysisRecord>;
    getSunAnalysis(request: { analysisId: string }): Promise<FormaSunAnalysis>;
    getNoiseAnalysis(request: { analysisId: string }): Promise<FormaNoiseAnalysis>;
  };
}

export interface TriggerSunAnalysisOptions {
  forma: FormaAnalysisClient;
  selectedElementPaths: string[];
  month: number;
  date: number;
  rootUrn?: string;
}

export interface SunAnalysisResult {
  analysis: FormaAnalysisRecord;
  groundGrid?: FormaAnalysisGroundGrid;
}

export interface GetOrTriggerSunAnalysisOptions extends Omit<TriggerSunAnalysisOptions, 'forma'> {
  forma: FormaNativeAnalysisClient;
  elementUrnPrefix?: string;
}

export interface TriggerNoiseAnalysisOptions {
  forma: FormaNativeAnalysisClient;
  selectedElementPaths: string[];
  rootUrn?: string;
}

export interface NoiseAnalysisResult {
  analysis: FormaAnalysisRecord;
  groundGrid?: FormaAnalysisGroundGrid;
}

export interface NativeAnalysisSnapshotItem {
  analysisId: string;
  analysisType: string;
  status: string;
  elementUrn?: string;
  proposalId?: string;
  proposalRevision?: string;
  parameters?: Record<string, unknown>;
  groundGrid?: {
    format: 'float32-row-major' | 'uint8-row-major';
    hash?: string;
    maskHash?: string;
    width: number;
    height: number;
    x0: number;
    y0: number;
    resolution: number;
  };
}

export interface NativeAnalysisExchange {
  schemaVersion: 'native-analysis-exchange/v1';
  source: 'forma-sdk';
  project: {
    formaProjectId: string;
    proposalId: string;
    proposalRevision?: string;
    rootUrn: string;
  };
  analyses: readonly NativeAnalysisSnapshotItem[];
}

export async function listNativeAnalyses(
  options: { forma: FormaNativeAnalysisClient } & ListNativeAnalysesRequest,
): Promise<readonly FormaAnalysisRecord[]> {
  const request = buildListNativeAnalysesRequest(options);

  return options.forma.analysis.list(request);
}

export async function getNativeSunAnalysis(options: {
  forma: FormaNativeAnalysisClient;
  analysisId: string;
}): Promise<FormaSunAnalysis> {
  return options.forma.analysis.getSunAnalysis({ analysisId: options.analysisId });
}

export async function getNativeNoiseAnalysis(options: {
  forma: FormaNativeAnalysisClient;
  analysisId: string;
}): Promise<FormaNoiseAnalysis> {
  return options.forma.analysis.getNoiseAnalysis({ analysisId: options.analysisId });
}

export async function getNativeGroundGrid(options: {
  forma: FormaAnalysisClient;
  analysis: FormaAnalysisRecord;
}): Promise<FormaAnalysisGroundGrid | undefined> {
  return options.forma.analysis.getGroundGrid({ analysis: options.analysis });
}

export async function triggerSunAnalysis(
  options: TriggerSunAnalysisOptions,
): Promise<SunAnalysisResult> {
  const request =
    options.rootUrn === undefined
      ? {
          selectedElementPaths: options.selectedElementPaths,
          month: options.month,
          date: options.date,
        }
      : {
          selectedElementPaths: options.selectedElementPaths,
          month: options.month,
          date: options.date,
          rootUrn: options.rootUrn,
        };
  const analysis = await options.forma.analysis.triggerSun(request);
  const groundGrid = await options.forma.analysis.getGroundGrid({ analysis });

  return groundGrid === undefined ? { analysis } : { analysis, groundGrid };
}

export async function getOrTriggerSunAnalysis(
  options: GetOrTriggerSunAnalysisOptions,
): Promise<SunAnalysisResult> {
  const existing = await findReusableSunAnalysis(options);

  if (existing !== undefined) {
    const groundGrid = await getNativeGroundGrid({ forma: options.forma, analysis: existing });

    return groundGrid === undefined ? { analysis: existing } : { analysis: existing, groundGrid };
  }

  return triggerSunAnalysis(options);
}

export async function triggerNoiseAnalysis(
  options: TriggerNoiseAnalysisOptions,
): Promise<NoiseAnalysisResult> {
  const request =
    options.rootUrn === undefined
      ? {
          selectedElementPaths: options.selectedElementPaths,
        }
      : {
          selectedElementPaths: options.selectedElementPaths,
          rootUrn: options.rootUrn,
        };
  const analysis = await options.forma.analysis.triggerNoise(request);
  const groundGrid = await getNativeGroundGrid({ forma: options.forma, analysis });

  return groundGrid === undefined ? { analysis } : { analysis, groundGrid };
}

async function findReusableSunAnalysis(
  options: GetOrTriggerSunAnalysisOptions,
): Promise<FormaSunAnalysis | undefined> {
  const elementUrnPrefix = options.elementUrnPrefix ?? options.rootUrn;
  const listOptions =
    elementUrnPrefix === undefined
      ? {
          forma: options.forma,
          analysisTypes: ['sun'],
        }
      : {
          forma: options.forma,
          analysisTypes: ['sun'],
          elementUrnPrefix,
        };
  const analyses = await listNativeAnalyses(listOptions);
  const candidates = analyses.filter(
    (analysis) => analysis.analysisType === 'sun' && analysis.status === 'SUCCEEDED',
  );

  for (const candidate of candidates) {
    const sunAnalysis = await getNativeSunAnalysis({
      forma: options.forma,
      analysisId: candidate.analysisId,
    });

    if (
      sunAnalysis.parameters.sunDate.month === options.month &&
      sunAnalysis.parameters.sunDate.date === options.date &&
      haveSameStringMembers(
        sunAnalysis.parameters.selectedElementPaths,
        options.selectedElementPaths,
      )
    ) {
      return sunAnalysis;
    }
  }

  return undefined;
}

function buildListNativeAnalysesRequest(options: ListNativeAnalysesRequest): {
  authcontext?: string;
  elementUrnPrefix?: string;
  analysisTypes?: string[];
} {
  const request: {
    authcontext?: string;
    elementUrnPrefix?: string;
    analysisTypes?: string[];
  } = {};

  if (options.authcontext !== undefined) {
    request.authcontext = options.authcontext;
  }

  if (options.elementUrnPrefix !== undefined) {
    request.elementUrnPrefix = options.elementUrnPrefix;
  }

  if (options.analysisTypes !== undefined) {
    request.analysisTypes = [...options.analysisTypes];
  }

  return request;
}

function haveSameStringMembers(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  return sortedLeft.every((value, index) => value === sortedRight[index]);
}

export type WindComfortScale =
  | 'lawson_lddc'
  | 'davenport'
  | 'lawson'
  | 'lawson_2001'
  | 'nen8100'
  | 'cstb';

export interface FormaWindDirectionData {
  direction: 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315;
  probability: number;
  weibull_parameters: {
    scale: number;
    shape: number;
  };
}

export interface FormaWindParameters {
  data: FormaWindDirectionData[];
  height: number;
  roughness: number;
}

export interface FormaHeightMaps {
  terrainHeightArray: number[];
  buildingAndTerrainHeightArray: number[];
  minHeight: number;
  maxHeight: number;
}

export interface FormaPredictiveAnalysisGroundGrid {
  grid: Float32Array;
  width: number;
  height: number;
  scale: {
    x: number;
    y: number;
  };
}

export interface FormaWindPredictionClient {
  prediction: {
    getWindParameters(): Promise<FormaWindParameters>;
    predictWind(request: {
      heightMaps: FormaHeightMaps;
      windRose: {
        data: FormaWindDirectionData[];
        height: number;
      };
      type: 'comfort';
      roughness: number;
      comfortScale: WindComfortScale;
    }): Promise<FormaPredictiveAnalysisGroundGrid>;
  };
}

export interface PredictRapidWindComfortOptions {
  forma: FormaWindPredictionClient;
  heightMaps: FormaHeightMaps;
  comfortScale: WindComfortScale;
  windParameters?: FormaWindParameters;
  roughness?: number;
}

export async function predictRapidWindComfort(
  options: PredictRapidWindComfortOptions,
): Promise<FormaPredictiveAnalysisGroundGrid> {
  const windParameters =
    options.windParameters ?? (await options.forma.prediction.getWindParameters());

  return options.forma.prediction.predictWind({
    heightMaps: options.heightMaps,
    windRose: {
      data: windParameters.data,
      height: windParameters.height,
    },
    type: 'comfort',
    roughness: options.roughness ?? windParameters.roughness,
    comfortScale: options.comfortScale,
  });
}

export interface FormaSunDateClient {
  sun: {
    getDate(): Promise<Date>;
    setDate(request: { date: Date }): Promise<void>;
  };
  project: {
    getTimezone(): Promise<string | undefined>;
  };
}

export interface SunDateOptions {
  forma: FormaSunDateClient;
}

export interface SetSunDateOptions extends SunDateOptions {
  date: Date;
}

export interface SunDateResult {
  date: Date;
  timezone?: string;
}

export async function getProjectSunDate(options: SunDateOptions): Promise<SunDateResult> {
  const [date, timezone] = await Promise.all([
    options.forma.sun.getDate(),
    options.forma.project.getTimezone(),
  ]);

  return timezone === undefined ? { date } : { date, timezone };
}

export async function setProjectSunDate(options: SetSunDateOptions): Promise<SunDateResult> {
  const timezone = await options.forma.project.getTimezone();
  await options.forma.sun.setDate({ date: options.date });

  return timezone === undefined ? { date: options.date } : { date: options.date, timezone };
}

export interface CapturableCanvas {
  toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export interface FormaCameraClient {
  camera: {
    capture(request: { width: number; height: number }): Promise<CapturableCanvas>;
  };
}

export interface CaptureCameraBlobOptions {
  forma: FormaCameraClient;
  width: number;
  height: number;
  mimeType?: string;
  quality?: number;
}

export async function captureCameraBlob(options: CaptureCameraBlobOptions): Promise<Blob> {
  const canvas = await options.forma.camera.capture({
    width: options.width,
    height: options.height,
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) {
          reject(new Error('Forma camera capture did not produce a Blob.'));
          return;
        }

        resolve(blob);
      },
      options.mimeType,
      options.quality,
    );
  });
}
